import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Tone } from "@/components/ui/badge";
import { ApiError, api } from "@/lib/api";
import { useKeys } from "@/lib/queries";
import { retryQuery } from "@/lib/session-cache";

/**
 * System updates (ACL-631, ADR-205 in platform): the console's changelog — what is new, what is
 * better, what was mended — on a page of its own at the foot of the sidebar.
 *
 * Not notifications. The bell is for what needs somebody now, one person's; an update is the whole
 * team's, read when they choose, and never reaches the bell, a toast or the tab's title. What is
 * new to a person is marked on the sidebar's link instead, against the date of the newest entry
 * they have opened the page since (`system_updates_seen_at` in their `ui_prefs`).
 *
 * An entry with no tenant is the platform's, posted by a superadmin and read by every tenant; one
 * with a tenant is that tenant's own, posted by its admin. The server says on each entry whether
 * the caller may change it (`editable`), so the page asks nothing of the role itself. The rules
 * here are pure, with tests.
 */

export type SystemUpdateKind = "feature" | "improvement" | "fix";

export interface SystemUpdate {
  id: number;
  /** Null: the platform's, read by every tenant. */
  tenant_id: number | null;
  kind: SystemUpdateKind;
  title: string;
  body: string;
  published_at: string;
  editable: boolean;
}

export interface SystemUpdateInput {
  kind: SystemUpdateKind;
  title: string;
  body: string;
  /** Left out: now (on a new entry), or as it was (on an edit). */
  published_at?: string;
  /** A superadmin's only: a tenant's own entry. Left out, the platform's. */
  tenant_id?: number | null;
}

/** The person's `ui_prefs` key: the date of the newest entry they had when they last opened the page. */
export const SEEN_PREF = "system_updates_seen_at";

export const KINDS: readonly { kind: SystemUpdateKind; label: string; tone: Tone }[] = [
  { kind: "feature", label: "New feature", tone: "accent" },
  { kind: "improvement", label: "Improvement", tone: "purple" },
  { kind: "fix", label: "Fix", tone: "success" },
];

export function kindOf(kind: string): { label: string; tone: Tone } {
  const known = KINDS.find((k) => k.kind === kind);
  return known ? { label: known.label, tone: known.tone } : { label: kind, tone: "neutral" };
}

/** Newest first, as the server sends them; kept here too, so an edited date moves its entry at once. */
export function newestFirst(updates: readonly SystemUpdate[]): SystemUpdate[] {
  return [...updates].sort(
    (a, b) => Date.parse(b.published_at) - Date.parse(a.published_at) || b.id - a.id,
  );
}

/** What the person last saw, from their prefs: an ISO date, or null if they never opened the page. */
export function seenAt(prefs: Record<string, unknown> | undefined): string | null {
  const value = prefs?.[SEEN_PREF];
  return typeof value === "string" && !Number.isNaN(Date.parse(value)) ? value : null;
}

/**
 * Whether the sidebar marks the page as having something new: an entry dated after the newest one
 * the person had when they last opened it. Undefined prefs (not loaded, or failed) mark nothing; a
 * person who never opened the page has everything new.
 */
export function hasUnseen(
  updates: readonly SystemUpdate[] | undefined,
  prefs: Record<string, unknown> | undefined,
): boolean {
  if (!updates || updates.length === 0 || prefs === undefined) return false;
  const seen = seenAt(prefs);
  if (seen === null) return true;
  return updates.some((u) => Date.parse(u.published_at) > Date.parse(seen));
}

/** What to record as seen on opening the page, or null when nothing newer has come. */
export function seenToRecord(
  updates: readonly SystemUpdate[],
  prefs: Record<string, unknown> | undefined,
): string | null {
  const newest = newestFirst(updates)[0];
  if (!newest || prefs === undefined) return null;
  const seen = seenAt(prefs);
  return seen !== null && Date.parse(seen) >= Date.parse(newest.published_at)
    ? null
    : newest.published_at;
}

/** "Oct 20, 2026": an entry is dated by its day. */
export function formatUpdateDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** An `<input type="date">` value (`2026-10-20`) for an entry's date, in this browser's zone. */
export function dateInputValue(iso: string | undefined, now: Date = new Date()): string {
  const d = iso ? new Date(iso) : now;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The `published_at` to send for a picked day. Today is now, so a new entry goes above the day's
 * others; another day is its noon in this browser's zone, which no zone a team sits in moves to
 * the next or the day before.
 */
export function publishedAtFor(day: string, now: Date = new Date()): string {
  if (day === dateInputValue(undefined, now)) return now.toISOString();
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y!, m! - 1, d!, 12).toISOString();
}

// --- the server ----------------------------------------------------------------------------------

function useSystemUpdatesKey() {
  const keys = useKeys();
  return keys.scoped(["system-updates"]);
}

/**
 * The changelog. Read by the sidebar's link too, to mark what is new, so it is asked once a session
 * and kept for five minutes. A 404 is a core-api from before ACL-631: `available` is then false,
 * and the link stays hidden.
 */
export function useSystemUpdates() {
  const key = useSystemUpdatesKey();
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.get<SystemUpdate[]>("/system-updates"),
    staleTime: 5 * 60_000,
    // A missing route is not asked again: it is the answer.
    retry: (count, e) => !(e instanceof ApiError && e.status === 404) && retryQuery(count, e),
  });
  const missing = query.error instanceof ApiError && query.error.status === 404;
  // Another failure is not a missing route: the link stays, and the page says what went wrong.
  return { ...query, available: missing ? false : query.isPending ? undefined : true };
}

function useInvalidateSystemUpdates() {
  const key = useSystemUpdatesKey();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: key });
}

export function useCreateSystemUpdate() {
  const invalidate = useInvalidateSystemUpdates();
  return useMutation({
    mutationFn: (input: SystemUpdateInput) => api.post<SystemUpdate>("/system-updates", input),
    onSuccess: invalidate,
  });
}

export function useUpdateSystemUpdate() {
  const invalidate = useInvalidateSystemUpdates();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<SystemUpdateInput> & { id: number }) =>
      api.put<SystemUpdate>(`/system-updates/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteSystemUpdate() {
  const invalidate = useInvalidateSystemUpdates();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/system-updates/${id}`),
    onSuccess: invalidate,
  });
}
